import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { formatDateDMY } from "../../../utils/dateFormat";
import {
  LOAN_PAYMENT_METHODS,
  deleteDriverLoan,
  deleteDriverLoanRepayment,
  getDriverLoanOverview,
  recordDriverLoanRepayment,
  saveDriverLoan,
} from "../../../services/driverLoanApi";

const todayIso = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const blankLoan = () => ({
  driverId: "",
  amount: "",
  loanDate: todayIso(),
  paymentMethod: "CASH",
  referenceNumber: "",
  note: "",
});

const blankRepayment = () => ({
  amount: "",
  repaymentDate: todayIso(),
  paymentMethod: "CASH",
  referenceNumber: "",
  note: "",
});

const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const methodLabel = (value) => String(value || "").replaceAll("_", " ");
const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

export default function DriverLoansSection({ drivers }) {
  const [open, setOpen] = useState(false);
  const [overview, setOverview] = useState(null);
  const [includeSettled, setIncludeSettled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loanModal, setLoanModal] = useState(null);
  const [repaymentModal, setRepaymentModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOverview(await getDriverLoanOverview({ includeSettled }));
    } catch (error) {
      toast.error(errorMessage(error, "Could not load driver loans"));
    } finally {
      setLoading(false);
    }
  }, [includeSettled]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const saveLoan = async () => {
    const value = loanModal;
    if (!value.driverId) {
      toast.error("Pick a driver");
      return;
    }
    if (!Number(value.amount)) {
      toast.error("Enter an amount greater than zero");
      return;
    }
    setSaving(true);
    try {
      await saveDriverLoan({ ...value, driverId: Number(value.driverId), amount: Number(value.amount) });
      setLoanModal(null);
      await load();
      toast.success("Loan saved");
    } catch (error) {
      toast.error(errorMessage(error, "Could not save loan"));
    } finally {
      setSaving(false);
    }
  };

  const saveRepayment = async () => {
    const { loan, value } = repaymentModal;
    if (!Number(value.amount)) {
      toast.error("Enter an amount greater than zero");
      return;
    }
    setSaving(true);
    try {
      await recordDriverLoanRepayment(loan.id, { ...value, amount: Number(value.amount) });
      setRepaymentModal(null);
      await load();
      toast.success("Repayment recorded");
    } catch (error) {
      toast.error(errorMessage(error, "Could not record repayment"));
    } finally {
      setSaving(false);
    }
  };

  const removeLoan = async (loan) => {
    if (!window.confirm(`Delete the ${money(loan.amount)} loan to ${loan.driverName}?`)) return;
    try {
      await deleteDriverLoan(loan.id);
      await load();
      toast.success("Loan deleted");
    } catch (error) {
      toast.error(errorMessage(error, "Could not delete loan"));
    }
  };

  const removeRepayment = async (loan, repayment) => {
    if (!window.confirm(`Delete the ${money(repayment.amount)} repayment?`)) return;
    try {
      await deleteDriverLoanRepayment(loan.id, repayment.id);
      await load();
      toast.success("Repayment deleted");
    } catch (error) {
      toast.error(errorMessage(error, "Could not delete repayment"));
    }
  };

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div role="button" className="d-flex align-items-center gap-2" onClick={() => setOpen((v) => !v)}>
          <i className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"}`} aria-hidden="true" />
          <div>
            <div className="fw-semibold">
              Driver Loans
              {overview && <> · <span className="text-danger">{money(overview.outstanding)} outstanding</span></>}
            </div>
            <div className="small text-muted">
              Goodwill advances given to drivers. Interest free and separate from trip payments - here so an
              amount handed over is not forgotten.
            </div>
          </div>
        </div>
        <Button size="sm" variant="outline-secondary" onClick={() => setLoanModal(blankLoan())}>+ Driver Loan</Button>
      </div>

      {open && (
        <div className="mt-3">
          <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
            {overview && (
              <>
                <div className="border rounded px-3 py-2">
                  <div className="small text-muted">Outstanding</div>
                  <div className="fw-semibold">{money(overview.outstanding)}</div>
                </div>
                <div className="border rounded px-3 py-2">
                  <div className="small text-muted">Given</div>
                  <div className="fw-semibold">{money(overview.totalLent)}</div>
                </div>
                <div className="border rounded px-3 py-2">
                  <div className="small text-muted">Repaid</div>
                  <div className="fw-semibold">{money(overview.totalRepaid)}</div>
                </div>
                <div className="border rounded px-3 py-2">
                  <div className="small text-muted">Drivers</div>
                  <div className="fw-semibold">{overview.drivers}</div>
                </div>
              </>
            )}
            <Form.Check
              type="switch"
              label="Show settled loans"
              checked={includeSettled}
              onChange={(e) => setIncludeSettled(e.target.checked)}
            />
          </div>

          {loading ? (
            <div className="text-muted"><Spinner size="sm" className="me-2" />Loading driver loans...</div>
          ) : (
            <div className="table-responsive">
              <Table size="sm" hover className="small mb-0 align-middle bg-white">
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>#</th>
                    <th>Driver</th>
                    <th>Owner</th>
                    <th>Date</th>
                    <th className="text-end">Given</th>
                    <th className="text-end">Repaid</th>
                    <th className="text-end">Outstanding</th>
                    <th>Note</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview?.loans || []).map((loan, index) => (
                    <React.Fragment key={loan.id}>
                      <tr>
                        <td className="fw-semibold">{index + 1}</td>
                        <td>
                          <div className="fw-semibold">{loan.driverName}</div>
                          <div className="text-muted">{loan.driverPhone}</div>
                        </td>
                        <td>{loan.ownerName || <span className="text-muted">Individual</span>}</td>
                        <td>{formatDateDMY(loan.loanDate)}<div className="text-muted">{methodLabel(loan.paymentMethod)}</div></td>
                        <td className="text-end">{money(loan.amount)}</td>
                        <td className="text-end">{money(loan.totalRepaid)}</td>
                        <td className="text-end fw-semibold">
                          {loan.settled
                            ? <Badge bg="success">Settled</Badge>
                            : <span className="text-danger">{money(loan.outstanding)}</span>}
                        </td>
                        <td className="text-muted">{loan.note || "-"}</td>
                        <td className="text-end">
                          <div className="d-flex gap-1 justify-content-end">
                            {!loan.settled && (
                              <Button
                                size="sm"
                                variant="outline-success"
                                onClick={() => setRepaymentModal({ loan, value: blankRepayment() })}
                              >
                                Repay
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => setLoanModal({ ...blankLoan(), ...loan, amount: String(loan.amount ?? "") })}
                            >
                              Edit
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => removeLoan(loan)}>Delete</Button>
                          </div>
                        </td>
                      </tr>
                      {(loan.repayments || []).map((repayment) => (
                        <tr key={`${loan.id}-${repayment.id}`} className="table-light">
                          <td></td>
                          <td colSpan={3} className="text-muted">
                            Repaid {formatDateDMY(repayment.repaymentDate)} · {methodLabel(repayment.paymentMethod)}
                            {repayment.referenceNumber ? ` · ${repayment.referenceNumber}` : ""}
                          </td>
                          <td></td>
                          <td className="text-end text-success">{money(repayment.amount)}</td>
                          <td></td>
                          <td className="text-muted">{repayment.note || "-"}</td>
                          <td className="text-end">
                            <Button size="sm" variant="outline-danger" onClick={() => removeRepayment(loan, repayment)}>
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {!overview?.loans?.length && (
                    <tr><td colSpan={9} className="text-center text-muted">No driver loans recorded.</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}

      <Modal show={!!loanModal} onHide={() => !saving && setLoanModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">{loanModal?.id ? "Edit Driver Loan" : "New Driver Loan"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loanModal && (
            <div className="row g-2">
              <div className="col-12">
                <Form.Label>Driver *</Form.Label>
                <Form.Select
                  value={loanModal.driverId || ""}
                  disabled={!!loanModal.id}
                  onChange={(e) => setLoanModal((old) => ({ ...old, driverId: e.target.value }))}
                >
                  <option value="">Select driver</option>
                  {(drivers || []).map((driver) => (
                    <option key={driver.id} value={driver.id}>
                      {driver.name} ({driver.phone}){driver.ownerName ? ` - ${driver.ownerName}` : ""}
                    </option>
                  ))}
                </Form.Select>
                {loanModal.id && (
                  <Form.Text className="text-muted">A loan cannot be moved to a different driver.</Form.Text>
                )}
              </div>
              <div className="col-6">
                <Form.Label>Amount (₹) *</Form.Label>
                <Form.Control
                  type="number" min="0" step="0.01"
                  value={loanModal.amount}
                  onChange={(e) => setLoanModal((old) => ({ ...old, amount: e.target.value }))}
                />
              </div>
              <div className="col-6">
                <Form.Label>Date *</Form.Label>
                <Form.Control
                  type="date" max={todayIso()}
                  value={loanModal.loanDate || ""}
                  onChange={(e) => setLoanModal((old) => ({ ...old, loanDate: e.target.value }))}
                />
              </div>
              <div className="col-6">
                <Form.Label>Paid By</Form.Label>
                <Form.Select
                  value={loanModal.paymentMethod || "CASH"}
                  onChange={(e) => setLoanModal((old) => ({ ...old, paymentMethod: e.target.value }))}
                >
                  {LOAN_PAYMENT_METHODS.map((method) => (
                    <option key={method} value={method}>{methodLabel(method)}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-6">
                <Form.Label>Reference / UTR</Form.Label>
                <Form.Control
                  value={loanModal.referenceNumber || ""}
                  onChange={(e) => setLoanModal((old) => ({ ...old, referenceNumber: e.target.value }))}
                />
              </div>
              <div className="col-12">
                <Form.Label>Note</Form.Label>
                <Form.Control
                  value={loanModal.note || ""}
                  placeholder="Why the advance was given"
                  onChange={(e) => setLoanModal((old) => ({ ...old, note: e.target.value }))}
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={saving} onClick={() => setLoanModal(null)}>Cancel</Button>
          <Button disabled={saving} onClick={saveLoan}>{saving ? "Saving..." : "Save Loan"}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!repaymentModal} onHide={() => !saving && setRepaymentModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Record Repayment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {repaymentModal && (
            <div className="row g-2">
              <div className="col-12 small text-muted">
                {repaymentModal.loan.driverName} · outstanding {money(repaymentModal.loan.outstanding)}
              </div>
              <div className="col-6">
                <Form.Label>Amount (₹) *</Form.Label>
                <Form.Control
                  type="number" min="0" step="0.01"
                  value={repaymentModal.value.amount}
                  onChange={(e) => setRepaymentModal((old) => ({ ...old, value: { ...old.value, amount: e.target.value } }))}
                />
              </div>
              <div className="col-6">
                <Form.Label>Date *</Form.Label>
                <Form.Control
                  type="date" max={todayIso()}
                  value={repaymentModal.value.repaymentDate}
                  onChange={(e) => setRepaymentModal((old) => ({ ...old, value: { ...old.value, repaymentDate: e.target.value } }))}
                />
              </div>
              <div className="col-6">
                <Form.Label>Received By</Form.Label>
                <Form.Select
                  value={repaymentModal.value.paymentMethod}
                  onChange={(e) => setRepaymentModal((old) => ({ ...old, value: { ...old.value, paymentMethod: e.target.value } }))}
                >
                  {LOAN_PAYMENT_METHODS.map((method) => (
                    <option key={method} value={method}>{methodLabel(method)}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-6">
                <Form.Label>Reference / UTR</Form.Label>
                <Form.Control
                  value={repaymentModal.value.referenceNumber}
                  onChange={(e) => setRepaymentModal((old) => ({ ...old, value: { ...old.value, referenceNumber: e.target.value } }))}
                />
              </div>
              <div className="col-12">
                <Form.Label>Note</Form.Label>
                <Form.Control
                  value={repaymentModal.value.note}
                  onChange={(e) => setRepaymentModal((old) => ({ ...old, value: { ...old.value, note: e.target.value } }))}
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={saving} onClick={() => setRepaymentModal(null)}>Cancel</Button>
          <Button disabled={saving} onClick={saveRepayment}>{saving ? "Saving..." : "Record Repayment"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
